import { DatePipe } from '@angular/common';
import { Component, OnInit, inject, input, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { take } from 'rxjs';

import { Logger } from '../../../core/logging/logger.service';
import { NumeronCollectionModel } from '../../../features/numerons/models/numeron.model';
import { NumeronsService } from '../../../features/numerons/services/numerons.service';

@Component({
  selector: 'app-collection-feed-card',
  imports: [DatePipe, RouterLink],
  templateUrl: './collection-feed-card.component.html',
})
export class CollectionFeedCardComponent implements OnInit {
  private readonly numeronsService = inject(NumeronsService);
  private readonly logger = inject(Logger);
  private readonly router = inject(Router);
  public readonly collection = input.required<NumeronCollectionModel>();
  public readonly isMyNumerons = input(false);
  public readonly isMyNumeronsByTopic = input(false);
  public readonly editReturnUrl = input('/mynumerons');
  protected readonly collectionLikes = signal(0);
  protected readonly isCopying = signal(false);
  protected readonly copyErrorMessage = signal('');

  public ngOnInit(): void {
    this.collectionLikes.set(this.collection().likes ?? 0);
  }

  protected collectionHref(collectionId: string): string {
    return this.isMyNumerons()
      ? `/mynumerons/collection/${collectionId}`
      : `/numerons/collection/${collectionId}`;
  }

  protected byPublisherHref(userId?: string): string {
    return userId ? `/numerons/bypublisher/${userId}` : '/';
  }

  protected byTopicHref(topicId?: string): string {
    if (!topicId) {
      return '/';
    }

    return this.isMyNumerons() ? `/mynumerons/bytopic/${topicId}` : `/numerons/bytopic/${topicId}`;
  }

  protected byCategoryHref(category: string): string {
    const encodedCategory = encodeURIComponent(category);
    return this.isMyNumerons()
      ? `/mynumerons/bycategory/${encodedCategory}`
      : `/numerons/bycategory/${encodedCategory}`;
  }

  protected editHref(collectionId: string): string {
    return `/mynumerons/collection/${collectionId}/edit`;
  }

  protected deleteHref(collectionId: string): string {
    return `/mynumerons/${collectionId}/delete`;
  }

  protected editQueryParams(): { returnTo: string } {
    return { returnTo: this.editReturnUrl() };
  }

  protected manageQueryParams(): { returnTo: string } {
    return { returnTo: this.editReturnUrl() };
  }

  protected deleteQueryParams(collection: NumeronCollectionModel): {
    returnTo: string;
    itemTitle: string;
  } {
    return {
      returnTo: this.editReturnUrl(),
      itemTitle: collection.title?.trim() || '',
    };
  }

  protected async copyCollection(): Promise<void> {
    if (this.isCopying()) {
      return;
    }

    this.isCopying.set(true);
    this.copyErrorMessage.set('');

    this.numeronsService
      .copyCollection(this.collection()._id)
      .pipe(take(1))
      .subscribe({
        next: async (collection) => {
          this.isCopying.set(false);
          await this.router.navigate(['/mynumerons/collection', collection._id], {
            queryParams: {
              returnTo: this.editReturnUrl(),
            },
          });
        },
        error: (error) => {
          this.isCopying.set(false);
          this.copyErrorMessage.set('Oops, could not copy Collection!');
          this.logger.warn('Failed to copy collection.', {
            feature: 'numerons',
            operation: 'copy-collection',
            severity: 'medium',
            error,
          });
        },
      });
  }

  protected hasExternalSource(url?: string): boolean {
    return typeof url === 'string' && url.trim().length > 0;
  }

  protected displayImage(collection: NumeronCollectionModel): string | undefined {
    if (collection.assetPath && collection.assetPath.trim().length > 0) {
      return collection.assetPath;
    }

    if (Number(collection.numerons[0]?.type) === 0) {
      return collection.numerons[0].assetPath;
    }

    return undefined;
  }

  protected likeCollection(): void {
    this.numeronsService
      .likeCollection(this.collection()._id)
      .pipe(take(1))
      .subscribe({
        next: (response: unknown) => {
          const nModified = Number(
            (response as { nModified?: number | string; result?: { nModified?: number | string } })
              ?.nModified ??
              (response as { result?: { nModified?: number | string } })?.result?.nModified ??
              0,
          );

          if (nModified === 1) {
            this.collectionLikes.update((currentLikes) => currentLikes + 1);
          }
        },
        error: (error) => {
          this.logger.warn('Failed to like collection.', {
            feature: 'numerons',
            operation: 'like-collection',
            severity: 'medium',
            error,
          });
        },
      });
  }
}
